import { Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ExamStatusChip from 'Components/ExamStatusChip';

import { iclApi, subjectsApi } from '../../../api';
import copySvg from '../../../assets/img/copy.svg';
import { getStudents, getTeachers } from '../../../Common/Database';
import { tabs } from '../../../constants/checlistTableTabs';
import { examTablePopupsNames } from '../../../constants/examTablePopupsNames';
import { tableSize } from '../../../constants/tableSize';
import { Exam, Option } from '../../../shared/types';
import { PersonOption } from '../../../shared/types/works';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import { LoadingError } from '../../LoadingError';
import ExamFilters from '../ExamFilters';
import TableTabs from '../TableTabs';
import PersentageText from 'Components/PersentageText';
import { formatIclTime, sortIclsByTime } from '../exam-helpers';
import moment from 'moment';

const adminTabs = [
  { tabName: 'library', tabKey: tabs.all },
  { tabName: 'For Consideration', tabKey: tabs.consideration },
  { tabName: 'Templates', tabKey: tabs.template },
];

export default function ExamTableAdmin() {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabQuery = searchParams.get('tab');

  const [icls, setIcls] = useState<Exam[]>([]);
  const [iclsForShow, setIclsForShow] = useState<Exam[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<string>('');
  const [idForManipulation, setIdForManipulation] = useState<string>('');
  const [tab, setTab] = useState<string>(tabs.all);
  const [page, setPage] = useState(1);
  const [students, setStudents] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<Option[] | null>(null);
  const [click, setClick] = useState(false);

  const [subjectFilter, setSubjectFilter] = useState<Option | null>(null);
  const [studentFilter, setStudentFilter] = useState<PersonOption | null>(null);
  const [teacherFilter, setTeacherFilter] = useState<PersonOption | null>(null);
  const [classFilter, setClassFilter] = useState<string | null>(null);

  const getEmptyMessage = () => {
    switch (tab) {
      case tabs.all:
        return t('noExams');

      case tabs.consideration:
        return t('noConsideration');

      case tabs.template:
        return t('noExamTemplates');

      default:
        return '';
    }
  };

  useEffect(() => {
    setTab(tabQuery || tabs.all);
  }, [tabQuery]);

  useEffect(() => {
    const updateSubjects = (subjects: Option[]) => setSubjects(subjects);
    const unsubscribe = subjectsApi.subscribeToChanges(updateSubjects);

    return () => {
      unsubscribe();
    };
  }, []);

  const fetchTeachersListData = async () => {
    const teacherList = await getTeachers();
    if (teacherList) {
      const mapList = Object.values(teacherList);
      setTeachers(mapList);
    }
  };

  useEffect(() => {
    if (iclsForShow.length === 0 && page > 1) {
      setPage(page - 1);
    }
  }, [iclsForShow]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      try {
        const res = getStudents();
        res.then(resp => {
          const arrStudents = Object.values(resp);
          setStudents(arrStudents);
        });
      } catch (error) {
        console.log(error);
      }

      setIsLoading(false);
    };

    init();

    fetchTeachersListData();
  }, []);

  useEffect(() => {
    const unsubscribe = iclApi.subscribeToAdminExams({ setNewICLs: setIcls });
    setIsLoading(false);

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.consideration) {
      const unsubscribe = iclApi.subscribeToAdminConsiderationExams({
        setNewICLs: setIcls,
        classFilter,
        subjectFilter: subjectFilter?.id,
      });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.template) {
      const unsubscribe = iclApi.subscribeToTemplateExams({
        setNewICLs: setIcls,
        classFilter,
        subjectFilter: subjectFilter?.id,
      });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.all) {
      const unsubscribe = iclApi.subscribeToAdminExams({
        setNewICLs: setIcls,
        classFilter,
        subjectFilter: subjectFilter?.id,
        teacherFilter,
        studentFilter,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [click, tab]);

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.consideration) {
      const unsubscribe = iclApi.subscribeToAdminConsiderationExams({ setNewICLs: setIcls });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.template) {
      const unsubscribe = iclApi.subscribeToTemplateExams({ setNewICLs: setIcls });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.all) {
      const unsubscribe = iclApi.subscribeToAdminExams({ setNewICLs: setIcls });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [tab]);

  const handleNavigateToExamUpdate = (id: string) => {
    navigate('/exam-update', { state: { ICLId: id } });
  };

  const handleDeleteICL = async (id: string) => {
    setIsRemovingLoading(true);

    try {
      await iclApi.removeICL(id);
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong. Try again!'));
    }

    toast.success(t('Successfully removed!'));
    setIsModalOpen('');
    setIsRemovingLoading(false);
  };

  const handleOpenDeletePopup = (id: string) => {
    setIsModalOpen(examTablePopupsNames.delete);
    setIdForManipulation(id);
  };

  const handleConsiderTemplate = async (id: string, accept: boolean) => {
    const targetIcl = icls.find(it => it.id === id);

    if (targetIcl) {
      if (Object.prototype.hasOwnProperty.call(targetIcl, 'isConsideration')) {
        targetIcl['isConsideration'] = false;

        if (accept) {
          await iclApi.copyICLToAdmin(targetIcl).then(() => {
            setTab(tabs.all);
            toast.success(t('Successfully added!'));
          });
        } else {
          toast.success(t('Successfully denied!'));
        }
      }

      await iclApi.updateICL(targetIcl);
    }
  };

  const handlePopupActions = () => {
    switch (isModalOpen) {
      case 'delete':
        handleDeleteICL(idForManipulation);
        break;
      case 'copyAsAdmin':
        handleConsiderTemplate(idForManipulation, true);
        break;
      case 'denied':
        handleConsiderTemplate(idForManipulation, false);
    }

    setIsModalOpen('');
  };

  const actionPopupButtonTitle = useMemo(() => {
    switch (isModalOpen) {
      case examTablePopupsNames.delete:
        return 'delete';
      case examTablePopupsNames.copyAsAdmin:
        return 'copyAsTetherButton';
      case examTablePopupsNames.denied:
        return 'deniedButton';
      default:
        return '';
    }
  }, [isModalOpen]);

  const actionPopupTitle = useMemo(() => {
    switch (isModalOpen) {
      case examTablePopupsNames.delete:
        return 'want to delete exam';
      case examTablePopupsNames.copyAsAdmin:
        return 'copyAsAdmin';
      case examTablePopupsNames.denied:
        return 'denied';
      default:
        return '';
    }
  }, [isModalOpen]);

  const handleOpenCopyPopup = (id: string) => {
    setIsModalOpen(examTablePopupsNames.copyAsAdmin);
    setIdForManipulation(id);
  };

  const handleOpenDeniedPopup = (id: string) => {
    setIsModalOpen(examTablePopupsNames.denied);
    setIdForManipulation(id);
  };

  const renderConsiderationButton = (id: string, isConsideration: boolean, isTemplate: boolean) => {
    if (!isTemplate && isConsideration && tab === tabs.consideration) {
      return (
        <div className={'flex items-center gap-2'}>
          <button className="btn btn-primary more" onClick={() => handleOpenCopyPopup(id)}>
            {t('Accept')}
          </button>
          <button className="btn more" onClick={() => handleOpenDeniedPopup(id)}>
            {t('Denied')}
          </button>
        </div>
      );
    }

    if (!isTemplate && !isConsideration) {
      return (
        <Tooltip
          title={t('copyAsTether')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
            onClick={() => handleOpenCopyPopup(id)}
          >
            <img src={copySvg} alt="copy" />
          </button>
        </Tooltip>
      );
    }
  };

  const handleChangePage = async (newPage: number) => {
    setPage(newPage);
  };

  const handleApplyFilters = () => {
    setClick(!click);
  };

  const handleResetFilters = () => {
    setClick(!click);
    setTeacherFilter(null);
    setStudentFilter(null);
    setSubjectFilter(null);
    setClassFilter(null);
  };

  const handleChangeTab = (tab: string) => {
    setTab(tab);
    setPage(1);
    handleResetFilters();
  };

  const getFIOFromEmail = (email: string) => {
    for (const student of students) {
      if (student.email?.replace(/\./g, '') === email) {
        return student.FIO;
      }
    }

    for (const teacher of teachers) {
      if (teacher.email?.replace(/\./g, '') === email) {
        return teacher.FIO;
      }
    }

    return email;
  };

  useEffect(() => {
    const start = (page - 1) * tableSize;
    const end = start + tableSize;

    setIclsForShow(sortIclsByTime(icls).slice(start, end));
  }, [icls, page, tab]);

  const renderRows = () => {
    return iclsForShow.map(
      ({
        id,
        theme,
        studentEmail,
        teacherEmail,
        subject,
        class: studentClass,
        isConsideration,
        isTemplate,
        status,
        time,
        totalPoint,
      }) => {
        const formattedTime = formatIclTime(time);

        return (
          <TableRow key={id}>
            <TableCell
              sx={{
                width: 180,
                minWidth: 180,
                maxWidth: 180,
                overflow: 'hidden',
                position: 'sticky',
                left: 0,
                backgroundColor: 'white',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {theme}
            </TableCell>

            <TableCell sx={{ width: 120, minWidth: 120 }}>{t(studentClass)}</TableCell>

            <TableCell sx={{ width: 180, minWidth: 180 }}>
              {platformLang === 'ua' ? subject.name : subject.ruName}
            </TableCell>

            {tab === tabs.all && (
              <>
                <TableCell sx={{ width: 150, minWidth: 150 }}>
                  {studentEmail ? getFIOFromEmail(studentEmail) : ''}
                </TableCell>

                <TableCell sx={{ width: 210, minWidth: 210 }}>
                  <ExamStatusChip value={status} />
                </TableCell>

                <TableCell sx={{ width: 90, minWidth: 90 }}>
                  <PersentageText value={totalPoint ? Number(totalPoint) : 0} />
                </TableCell>

                <TableCell
                  sx={{
                    width: 100,
                    minWidth: 100,
                    textAlign: 'center',
                  }}
                >
                  <div className="flex flex-col items-center whitespace-nowrap leading-[18px]">
                    <span>{formattedTime.date}</span>
                    <span>{formattedTime.time}</span>
                  </div>
                </TableCell>
              </>
            )}

            {tab === tabs.all && (
              <TableCell sx={{ width: 140, minWidth: 140 }}>
                {teacherEmail ? getFIOFromEmail(teacherEmail) : ''}
              </TableCell>
            )}

            <TableCell sx={{ width: 230, minWidth: 230 }}>
              <div className="flex flex-row items-center justify-end gap-[10px] whitespace-nowrap [&>*]:!text-[16px] lg:[&>*]:!text-[18px]">
                <button className="btn btn-primary more" onClick={() => handleNavigateToExamUpdate(id)}>
                  {t('editing')}
                </button>

                {renderConsiderationButton(id, isConsideration ?? false, isTemplate)}

                {isTemplate && (
                  <Tooltip
                    title={t('deleteExam')}
                    componentsProps={{
                      popper: {
                        sx: {
                          ['& .MuiTooltip-arrow']: {
                            color: '#9CDFFC',
                          },
                          ['& .MuiTooltip-tooltip']: {
                            backgroundColor: '#9CDFFC',
                            color: '#000000',
                            fontSize: '14px',
                            padding: '14px',
                            borderRadius: '8px',
                          },
                        },
                      },
                    }}
                    arrow
                  >
                    <button
                      className="btn more small h-[40px] w-[40px] rounded-[8px]"
                      onClick={() => handleOpenDeletePopup(id)}
                    >
                      <span className="icon icon-delete text-red-600" />
                    </button>
                  </Tooltip>
                )}
              </div>
            </TableCell>
          </TableRow>
        );
      },
    );
  };

  return (
    <LoadingError isLoading={isLoading && !icls}>
      <div>
        <div className="mb-[20px] flex gap-[8px] lg:block">
          <TableTabs setTab={handleChangeTab} tab={tab} configArr={adminTabs} />

          {students && teachers && subjects && (
            <ExamFilters
              subject={subjectFilter}
              student={studentFilter}
              teacher={teacherFilter}
              studentClass={classFilter}
              setClassFilter={setClassFilter}
              setStudentFilter={setStudentFilter}
              setTeacherFilter={setTeacherFilter}
              setSubjectFilter={setSubjectFilter}
              teachers={teachers}
              students={students}
              handleApply={handleApplyFilters}
              handleReset={handleResetFilters}
              click={click}
              subjects={subjects}
              tab={tab}
            />
          )}
        </div>
        <div className="mb-[20px] rounded-[24px] bg-white [box-shadow:10px_10px_0px_0px_#EBEAE7] lg:px-[20px] lg:py-[10px]">
          <TableContainer sx={{ borderRadius: '24px' }}>
            <Table aria-label="simple table" sx={{ minWidth: tab === tabs.all ? 1400 : 720 }}>
              {iclsForShow.length > 0 && (
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: 180,
                        minWidth: 180,
                        maxWidth: 180,
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        borderTopLeftRadius: '24px',
                      }}
                    >
                      {t('theme')}
                    </TableCell>

                    <TableCell sx={{ width: 120, minWidth: 120 }}>{t('class')}</TableCell>

                    <TableCell sx={{ width: 180, minWidth: 180 }}>{t('subject')}</TableCell>

                    {tab === tabs.all && (
                      <>
                        <TableCell sx={{ width: 150, minWidth: 150 }}>{t('alreadySend')}</TableCell>

                        <TableCell sx={{ width: 210, minWidth: 210 }}>{t('exam status')}</TableCell>

                        <TableCell sx={{ width: 90, minWidth: 90 }}>{t('exam grade')}</TableCell>

                        <TableCell
                          sx={{
                            width: 100,
                            minWidth: 100,
                            textAlign: 'center',
                          }}
                        >
                          {t('time')}
                        </TableCell>
                      </>
                    )}

                    {tab === tabs.all && (
                      <TableCell
                        sx={{
                          width: 370,
                          minWidth: 370,
                        }}
                        colSpan={2}
                      >
                        {t('Teacher')}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
              )}

              {iclsForShow.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {getEmptyMessage()}
                  </TableCell>
                </TableRow>
              )}

              <TableBody>{renderRows()}</TableBody>
            </Table>
          </TableContainer>
        </div>

        {iclsForShow.length > 0 && (
          <Stack alignItems="center">
            <Pagination
              sx={{
                '& .MuiPaginationItem-page.Mui-selected': {
                  backgroundColor: 'white',
                  border: '1px solid #52C7FA',
                },
                '& .MuiPaginationItem-page': {
                  color: '#656D7B',
                },
              }}
              shape={'rounded'}
              page={page}
              onChange={(_, page) => handleChangePage(page)}
              count={Math.ceil(icls.length / tableSize)}
            />
          </Stack>
        )}

        <ItemActionModal
          isOpen={Boolean(isModalOpen)}
          close={() => setIsModalOpen('')}
          title={actionPopupTitle}
          onHandle={() => handlePopupActions()}
          isSubmitButtonDisabled={isRemovingLoading}
          buttonTitle={actionPopupButtonTitle}
        />
      </div>
    </LoadingError>
  );
}
